import React, { useState, useEffect } from 'react';
import './App.css';
import WhiskyList from './components/WhiskyList';
import RatingForm from './components/RatingForm';
import AddWhiskyForm from './components/AddWhiskyForm';
import EditWhiskyForm from './components/EditWhiskyForm';
import Scoreboard from './components/Scoreboard';

function App() {
  const [currentView, setCurrentView] = useState('list');
  const [whiskies, setWhiskies] = useState([]);
  const [ratings, setRatings] = useState({});
  const [selectedWhisky, setSelectedWhisky] = useState(null);
  const [editingWhisky, setEditingWhisky] = useState(null);

  // Cookie utility functions
  const setCookie = (name, value, days = 365) => {
    const expires = new Date();
    expires.setTime(expires.getTime() + (days * 24 * 60 * 60 * 1000));
    document.cookie = `${name}=${encodeURIComponent(JSON.stringify(value))};expires=${expires.toUTCString()};path=/`;
  };

  const getCookie = (name) => {
    const nameEQ = name + "=";
    const ca = document.cookie.split(';');
    for (let i = 0; i < ca.length; i++) {
      let c = ca[i];
      while (c.charAt(0) === ' ') c = c.substring(1, c.length);
      if (c.indexOf(nameEQ) === 0) {
        try {
          return JSON.parse(decodeURIComponent(c.substring(nameEQ.length, c.length)));
        } catch (e) {
          return null;
        }
      }
    }
    return null;
  };

  // Load data from cookies on app start
  useEffect(() => {
    const savedRatings = getCookie('whisky_ratings');
    const savedWhiskies = getCookie('whisky_collection');
    
    if (savedRatings) {
      setRatings(savedRatings);
    }
    
    if (savedWhiskies) {
      setWhiskies(savedWhiskies);
    }
  }, []);

  // Save data to cookies whenever they change
  useEffect(() => {
    if (Object.keys(ratings).length > 0) {
      setCookie('whisky_ratings', ratings);
    }
  }, [ratings]);

  useEffect(() => {
    if (whiskies.length > 0) {
      setCookie('whisky_collection', whiskies);
    }
  }, [whiskies]);

  const handleWhiskySelect = (whisky) => {
    setSelectedWhisky(whisky);
    setCurrentView('rating');
  };

  const handleRatingSubmit = (whiskyId, rating) => {
    setRatings(prev => ({
      ...prev,
      [whiskyId]: {
        ...rating,
        timestamp: new Date().toISOString()
      }
    }));
    setCurrentView('list');
    setSelectedWhisky(null);
  };

  const handleAddWhisky = (newWhisky, rating = null) => {
    setWhiskies(prev => [...prev, newWhisky]);
    
    // If a rating was provided, add it too
    if (rating) {
      setRatings(prev => ({
        ...prev,
        [newWhisky.id]: {
          ...rating,
          timestamp: new Date().toISOString()
        }
      }));
    }
    
    setCurrentView('list');
  };

  const handleEditWhisky = (whisky) => {
    setEditingWhisky(whisky);
    setCurrentView('edit');
  };

  const handleUpdateWhisky = (updatedWhisky) => {
    setWhiskies(prev => 
      prev.map(whisky => 
        whisky.id === updatedWhisky.id ? updatedWhisky : whisky
      )
    );
    setCurrentView('list');
    setEditingWhisky(null);
  };

  const handleDeleteWhisky = (whiskyId) => {
    // Remove the whisky from the collection
    setWhiskies(prev => prev.filter(whisky => whisky.id !== whiskyId));
    
    // Remove any rating for this whisky
    setRatings(prev => {
      const newRatings = { ...prev };
      delete newRatings[whiskyId];
      return newRatings;
    });
    
    // Clear any draft for this whisky
    const deleteCookie = (name) => {
      document.cookie = `${name}=;expires=Thu, 01 Jan 1970 00:00:00 UTC;path=/;`;
    };
    deleteCookie(`whisky_draft_${whiskyId}`);
  };

  const handleClearRating = (whiskyId) => {
    setRatings(prev => {
      const newRatings = { ...prev };
      delete newRatings[whiskyId];
      return newRatings;
    });
    
    // Also clear any draft for this whisky
    const deleteCookie = (name) => {
      document.cookie = `${name}=;expires=Thu, 01 Jan 1970 00:00:00 UTC;path=/;`;
    };
    deleteCookie(`whisky_draft_${whiskyId}`);
  };


  const renderCurrentView = () => {
    switch (currentView) {
      case 'rating':
        return (
          <RatingForm
            whisky={selectedWhisky}
            existingRating={ratings[selectedWhisky?.id]}
            onSubmit={handleRatingSubmit}
            onCancel={() => {
              setCurrentView('list');
              setSelectedWhisky(null);
            }}
          />
        );
      case 'add':
        return (
          <AddWhiskyForm
            onSubmit={handleAddWhisky}
            onCancel={() => setCurrentView('list')}
          />
        );
      case 'edit':
        return (
          <EditWhiskyForm
            whisky={editingWhisky}
            onSubmit={handleUpdateWhisky}
            onCancel={() => {
              setCurrentView('list');
              setEditingWhisky(null);
            }}
          />
        );
      case 'scoreboard':
        return (
          <Scoreboard
            whiskies={whiskies}
            ratings={ratings}
          />
        );
      default:
        return (
          <WhiskyList
            whiskies={whiskies}
            ratings={ratings}
            onWhiskySelect={handleWhiskySelect}
            onClearRating={handleClearRating}
            onAddWhisky={() => setCurrentView('add')}
            onDeleteWhisky={handleDeleteWhisky}
            onEditWhisky={handleEditWhisky}
          />
        );
    }
  };

  return (
    <div className="App">
      <header className="app-header">
        <h1>🥃 My Whisky Collection</h1>
        <p>Add and rate your whiskies</p>
      </header>

      <nav className="bottom-nav">
        <button
          className={`nav-btn ${currentView === 'list' ? 'active' : ''}`}
          onClick={() => setCurrentView('list')}
        >
          <span>📋</span>
          <span>Collection</span>
        </button>
        <button
          className={`nav-btn ${currentView === 'add' ? 'active' : ''}`}
          onClick={() => setCurrentView('add')}
        >
          <span>➕</span>
          <span>Add</span>
        </button>
        <button
          className={`nav-btn ${currentView === 'scoreboard' ? 'active' : ''}`}
          onClick={() => setCurrentView('scoreboard')}
        >
          <span>🏆</span>
          <span>Scores</span>
        </button>
      </nav>

      <main className="app-main">
        {renderCurrentView()}
      </main>
    </div>
  );
}

export default App;
